import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MyContext } from "../MainContext";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faChevronLeft, faEllipsisV, faBan, faReply, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Dropdown } from 'react-bootstrap';
import Header from '../components/header';
import Footer from '../components/footer';
import LoadingScreen from '../components/loading_screen';
import { timeAgo } from '../utils/functions';
import { backendUrl } from '../utils/variables';
import ChatLinkText from '../components/ChatLinkText';


export default function ChatPage() {
    const { user } = useContext(MyContext);
    const navigate = useNavigate();
    const { userId } = useParams();
    
    const [messages, setMessages] = useState([]);
    const [otherUser, setOtherUser] = useState(null);
    const [messageText, setMessageText] = useState('');
    const [replyingTo, setReplyingTo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [requestNotAccepted, setRequestNotAccepted] = useState(false);
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const nearBottomRef = useRef(true);
    const jumpTimerRef = useRef(null);
    const [jumpMessageId, setJumpMessageId] = useState(null);
    const [editingMessage, setEditingMessage] = useState(null);

    const API_BASE = backendUrl;

    const limit = 50;

    useEffect(() => {
        if (user && user.user_id && userId) {
            fetchMessages();
            fetchOtherUser();
            
            // Poll for new messages every 5 seconds
            const interval = setInterval(() => {
                fetchMessages(true);
            }, 5000);
            
            return () => clearInterval(interval);
        }
    }, [user, userId]);

    useEffect(() => {
        if (nearBottomRef.current) scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const scrollToQuotedMessage = (targetId) => {
        const root = messagesContainerRef.current;
        if (targetId == null || !root) return;
        const el = root.querySelector(`[data-message-id="${String(targetId)}"]`);
        if (!el) return;
        nearBottomRef.current = false;
        const next = root.scrollTop + (el.getBoundingClientRect().top - root.getBoundingClientRect().top) - 12;
        root.scrollTo({ top: Math.max(0, next), behavior: 'smooth' });
        setJumpMessageId(String(targetId));
        if (jumpTimerRef.current) clearTimeout(jumpTimerRef.current);
        jumpTimerRef.current = setTimeout(() => setJumpMessageId(null), 1200);
    };

    const onMessagesScroll = (event) => {
        const el = event.currentTarget;
        nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    };

    const fetchOtherUser = async () => {
        if (!user || !user.user_id || !userId) return;

        try {
            const response = await fetch(`${API_BASE}/get_user_by_id.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: userId
                }),
            });

            const data = await response.json();
            if (data.success && data.data) {
                setOtherUser({
                    user_id: data.data.user_id,
                    username: data.data.username,
                    name: data.data.name,
                    photo: data.data.photo
                });
            }
        } catch (error) {
            console.error('Error fetching other user:', error);
        }
    };

    const fetchMessages = async (refresh = false) => {
        if (!user || !user.user_id || !userId) return;

        const currentPage = refresh ? 1 : page;

        try {
            const response = await fetch(`${API_BASE}/get_direct_messages.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: user.user_id,
                    other_user_id: userId,
                    page: currentPage,
                    limit: limit
                }),
            });

            const data = await response.json();
            if (data.success) {
                setRequestNotAccepted(false);
                const messagesList = data.messages_list || [];
                
                if (refresh || currentPage === 1) {
                    setMessages(messagesList);
                } else {
                    setMessages(prev => [...messagesList, ...prev]);
                }
                
                if (messagesList.length < limit) {
                    setHasMore(false);
                }
            } else {
                if (data.messages && data.messages.includes('must be accepted')) {
                    setRequestNotAccepted(true);
                }
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
        } finally {
            setLoading(false);
        }
    };

    const saveEditedMessage = async () => {
        if (!editingMessage || !messageText.trim() || sending) return;
        const textToSave = messageText.trim();
        setSending(true);
        try {
            const response = await fetch(`${API_BASE}/edit_direct_message.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message_id: editingMessage.id,
                    user_id: user.user_id,
                    message: textToSave,
                }),
            });
            const data = await response.json();
            if (data.success) {
                setMessages(prev => prev.map(item => (
                    item.id == editingMessage.id
                        ? { ...item, message: data.data?.message || textToSave, edited_at: data.data?.edited_at || new Date().toISOString(), link_preview: data.data?.link_preview || null }
                        : item
                )));
                setEditingMessage(null);
                setMessageText('');
            } else {
                alert(data.messages || 'Could not edit message');
            }
        } catch (error) {
            alert('Could not edit message');
        } finally {
            setSending(false);
        }
    };

    const sendMessage = async () => {
        if (editingMessage) {
            saveEditedMessage();
            return;
        }
        if (!messageText.trim() || sending || !user || !user.user_id || !userId) return;

        setSending(true);

        try {
            const response = await fetch(`${API_BASE}/send_direct_message.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    sender_id: user.user_id,
                    receiver_id: userId,
                    message: messageText.trim(),
                    reply_to_message_id: replyingTo ? replyingTo.id : null
                }),
            });

            const data = await response.json();
            if (data.success) {
                setMessageText('');
                setReplyingTo(null); // Clear reply after sending
                // Use client's current time for immediate accurate display
                const now = new Date();
                const currentTimestamp = now.getFullYear() + '-' + 
                    String(now.getMonth() + 1).padStart(2, '0') + '-' + 
                    String(now.getDate()).padStart(2, '0') + ' ' + 
                    String(now.getHours()).padStart(2, '0') + ':' + 
                    String(now.getMinutes()).padStart(2, '0') + ':' + 
                    String(now.getSeconds()).padStart(2, '0');
                const currentTimestampUnix = Math.floor(now.getTime() / 1000);
                
                setMessages(prev => [...prev, {
                    id: data.data.message_id,
                    sender_id: user.user_id,
                    receiver_id: userId,
                    message: data.data.message,
                    reply_to_message_id: replyingTo ? replyingTo.id : null,
                    reply_to_message: replyingTo ? replyingTo.message : null,
                    reply_to_sender_username: replyingTo ? (replyingTo.sender_id == user.user_id ? user.username : otherUser?.username) : null,
                    created_at: currentTimestamp, // Use client time for accurate display
                    created_at_timestamp: currentTimestampUnix, // Also include Unix timestamp
                    sender_username: user.username,
                    sender_first_name: user.name?.split(' ')[0] || '',
                    sender_last_name: user.name?.split(' ')[1] || '',
                    sender_photo: user.photo,
                    link_preview: data.data?.link_preview || null
                }]);
                nearBottomRef.current = true;
                scrollToBottom();
            } else {
                alert(data.messages || 'Failed to send message');
            }
        } catch (error) {
            console.error('Error sending message:', error);
            alert('Network error. Please try again.');
        } finally {
            setSending(false);
        }
    };

    const handleBlock = async () => {
        if (!user || !user.user_id || !userId) return;
        
        if (!window.confirm('Are you sure you want to block this user? You will not be able to receive messages from them.')) {
            return;
        }

        try {
            const response = await fetch(`${API_BASE}/block_user.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    blocker_id: user.user_id,
                    blocked_id: userId
                }),
            });

            const data = await response.json();
            if (data.success) {
                alert('User blocked successfully');
                navigate('/messages');
            } else {
                alert(data.messages || 'Failed to block user');
            }
        } catch (error) {
            console.error('Error blocking user:', error);
            alert('Network error. Please try again.');
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    if (!user || !user.user_id) {
        return <LoadingScreen />;
    }

    if (loading && !otherUser) {
        return <LoadingScreen />;
    }

    return (
        <div className="container">
            <Header />
            
            <div className="container-fluid mt-5 pt-3" style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 120px)', paddingBottom: '0', marginBottom: '60px' }}>
                {/* Chat Header - Sticky */}
                <div 
                    className="d-flex align-items-center mb-3 border-bottom pb-2 bg-white"
                    style={{ 
                        position: 'sticky', 
                        top: '56px', 
                        zIndex: 100,
                        paddingTop: '15px',
                        paddingBottom: '15px',
                        marginTop: '-15px',
                        marginLeft: '-15px',
                        marginRight: '-15px',
                        paddingLeft: '15px',
                        paddingRight: '15px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }}
                >
                    <FontAwesomeIcon 
                        icon={faChevronLeft} 
                        className="me-3"
                        onClick={() => navigate('/messages')}
                        style={{ cursor: 'pointer' }}
                    />
                    {otherUser && (
                        <>
                            <img
                                src={otherUser.photo || `${process.env.PUBLIC_URL}/avatar.jpg`}
                                alt={otherUser.username}
                                className="rounded-circle me-2"
                                width="40"
                                height="40"
                            />
                            <div className="flex-grow-1">
                                <h6 className="mb-0">{otherUser.name}</h6>
                                <small className="text-muted">@{otherUser.username}</small>
                            </div>
                            <Dropdown>
                                <Dropdown.Toggle variant="link" bsPrefix="p-0">
                                    <FontAwesomeIcon icon={faEllipsisV} style={{ color: '#48887b' }} />
                                </Dropdown.Toggle>
                                <Dropdown.Menu align="end">
                                    <Dropdown.Item onClick={handleBlock}>
                                        <FontAwesomeIcon icon={faBan} className="me-2" />
                                        Block User
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </>
                    )}
                </div>

                {/* Messages Container */}
                <div 
                    ref={messagesContainerRef}
                    onScroll={onMessagesScroll}
                    className="flex-grow-1 overflow-auto"
                    style={{ flex: '1 1 auto', marginBottom: '10px', minHeight: '0', overflowY: 'auto', paddingBottom: '10px' }}
                >
                    {requestNotAccepted ? (
                        <div className="text-center py-5">
                            <p className="text-danger">Message request must be accepted before viewing messages.</p>
                            <button className="btn btn-primary mt-2" onClick={() => navigate('/messages/requests')}>
                                Go to Message Requests
                            </button>
                        </div>
                    ) : loading && messages.length === 0 ? (
                        <LoadingScreen />
                    ) : messages.length === 0 ? (
                        <div className="text-center py-5">
                            <p className="text-muted">No messages yet. Start the conversation!</p>
                        </div>
                    ) : (
                        <div className="messages-list">
                            {messages.map((message) => {
                                const isOwnMessage = message.sender_id == user.user_id;
                                const isReplyingToThis = replyingTo && replyingTo.id === message.id;
                                return (
                                    <div
                                        key={message.id}
                                        data-message-id={message.id}
                                        className={`d-flex mb-3 ${isOwnMessage ? 'justify-content-end' : 'justify-content-start'}`}
                                        style={String(jumpMessageId) === String(message.id) ? { backgroundColor: 'rgba(37, 211, 102, 0.22)', borderRadius: 8 } : undefined}
                                    >
                                        {!isOwnMessage && (
                                            <img
                                                src={message.sender_photo || `${process.env.PUBLIC_URL}/avatar.jpg`}
                                                alt={message.sender_username}
                                                className="rounded-circle me-2"
                                                width="30"
                                                height="30"
                                            />
                                        )}
                                        <div className={`message-bubble ${isOwnMessage ? 'own-message' : 'other-message'}`} style={{ position: 'relative' }}>
                                            {/* Reply Preview */}
                                            {message.reply_to_message_id && message.reply_to_message && (
                                                <div
                                                    className="reply-preview"
                                                    role="button"
                                                    onClick={() => scrollToQuotedMessage(message.reply_to_message_id)}
                                                    style={{
                                                    borderLeft: '3px solid #48887b',
                                                    paddingLeft: '10px',
                                                    marginBottom: '8px',
                                                    paddingTop: '5px',
                                                    paddingBottom: '5px',
                                                    backgroundColor: 'rgba(72, 136, 123, 0.1)',
                                                    borderRadius: '5px',
                                                    fontSize: '0.85em',
                                                    cursor: 'pointer'
                                                }}>
                                                    <div style={{ fontWeight: 'bold', color: '#48887b', marginBottom: '3px' }}>
                                                        {message.reply_to_sender_id == user.user_id 
                                                            ? 'You' 
                                                            : (message.reply_to_sender_username 
                                                                ? `@${message.reply_to_sender_username}` 
                                                                : (message.reply_to_sender_first_name 
                                                                    ? `${message.reply_to_sender_first_name} ${message.reply_to_sender_last_name || ''}`.trim()
                                                                    : 'User'))}
                                                    </div>
                                                    <div style={{ color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {message.reply_to_message.length > 50 
                                                            ? message.reply_to_message.substring(0, 50) + '...' 
                                                            : message.reply_to_message}
                                                    </div>
                                                </div>
                                            )}
                                            
                                            <ChatLinkText text={message.message} preview={message.link_preview} />
                                            <div className="d-flex justify-content-between align-items-center">
                                                <small className="text-muted" style={{ display: 'block' }}>
                                                    {message.edited_at ? <span style={{ color: '#9a9a9a', marginRight: 6 }}>edited</span> : null}
                                                    {(() => {
                                                        // Prioritize Unix timestamp from server
                                                        if (message.created_at_timestamp) {
                                                            const timestamp = typeof message.created_at_timestamp === 'string' 
                                                                ? parseFloat(message.created_at_timestamp) 
                                                                : message.created_at_timestamp;
                                                            return timeAgo(timestamp);
                                                        } else if (message.created_at) {
                                                            return timeAgo(message.created_at);
                                                        }
                                                        return 'now';
                                                    })()}
                                                </small>
                                                {isOwnMessage && !message.edited_at && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-link btn-sm p-0 ms-2"
                                                        style={{ color: '#9a9a9a', fontSize: '0.75em', textDecoration: 'none' }}
                                                        onClick={() => {
                                                            setReplyingTo(null);
                                                            setEditingMessage(message);
                                                            setMessageText(message.message || '');
                                                        }}
                                                    >
                                                        Edit
                                                    </button>
                                                )}
                                                <FontAwesomeIcon 
                                                    icon={faReply} 
                                                    className="ms-2"
                                                    style={{ 
                                                        fontSize: '0.75em', 
                                                        color: '#48887b',
                                                        opacity: isReplyingToThis ? 1 : 0.5
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setReplyingTo(isReplyingToThis ? null : message);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            <div ref={messagesEndRef} />
                        </div>
                    )}
                </div>

                {/* Message Input - Sticky at Bottom */}
                {!requestNotAccepted && (
                    <div 
                        className="border-top pt-2 bg-white"
                        style={{ 
                            position: 'sticky',
                            bottom: '60px',
                            zIndex: 100,
                            marginTop: 'auto',
                            paddingTop: '15px',
                            paddingBottom: '15px',
                            marginLeft: '-15px',
                            marginRight: '-15px',
                            paddingLeft: '15px',
                            paddingRight: '15px',
                            boxShadow: '0 -2px 4px rgba(0,0,0,0.05)'
                        }}
                    >
                        {editingMessage && (
                            <div className="mb-2 p-2 d-flex justify-content-between" style={{ backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                                <small style={{ color: '#9a9a9a' }}>Editing message</small>
                                <span style={{ cursor: 'pointer', color: '#666' }} onClick={() => { setEditingMessage(null); setMessageText(''); }}>×</span>
                            </div>
                        )}
                        {/* Reply Preview */}
                        {replyingTo && !editingMessage && (
                            <div className="mb-2 p-2" style={{
                                backgroundColor: '#f8f9fa',
                                borderRadius: '5px',
                                borderLeft: '3px solid #48887b',
                                fontSize: '0.9em'
                            }}>
                                <div className="d-flex justify-content-between align-items-start">
                                    <div className="flex-grow-1">
                                        <div style={{ fontWeight: 'bold', color: '#48887b', marginBottom: '3px' }}>
                                            Replying to {replyingTo.sender_id == user.user_id 
                                                ? 'yourself' 
                                                : (replyingTo.sender_username 
                                                    ? `@${replyingTo.sender_username}` 
                                                    : (replyingTo.sender_first_name 
                                                        ? `${replyingTo.sender_first_name} ${replyingTo.sender_last_name || ''}`.trim()
                                                        : 'user'))}
                                        </div>
                                        <div style={{ color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {replyingTo.message.length > 60 
                                                ? replyingTo.message.substring(0, 60) + '...' 
                                                : replyingTo.message}
                                        </div>
                                    </div>
                                    <FontAwesomeIcon 
                                        icon={faTimes} 
                                        className="ms-2"
                                        style={{ cursor: 'pointer', color: '#666' }}
                                        onClick={() => setReplyingTo(null)}
                                    />
                                </div>
                            </div>
                        )}
                        
                        <div className="d-flex align-items-end">
                            <textarea
                                className="form-control me-2"
                                rows="1"
                                placeholder={editingMessage ? "Edit your message..." : (replyingTo ? "Type a reply..." : "Type a message...")}
                                value={messageText}
                                onChange={(e) => setMessageText(e.target.value)}
                                onKeyPress={handleKeyPress}
                                style={{ resize: 'none' }}
                            />
                            <button
                                className="btn b-primary"
                                onClick={sendMessage}
                                disabled={!messageText.trim() || sending}
                                style={{ backgroundColor: '#48887b', borderColor: '#48887b' }}
                            >
                                <FontAwesomeIcon icon={faPaperPlane} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <Footer page="messages" />

            <style>{`
                .message-bubble {
                    max-width: 70%;
                    padding: 10px 15px;
                    border-radius: 18px;
                    word-wrap: break-word;
                }
                .own-message {
                    background-color: #FFF8DC;
                    color: #000000;
                }
                .other-message {
                    background-color: #f1f1f1;
                    color: #333;
                }
            `}</style>
        </div>
    );
}

